import { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, ImageOff, CheckCircle, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SEO } from '../components/SEO';
import projects, { categories } from '../data/projects';

function AnimatedCounter({ target, duration = 1500 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.round(eased * target));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count}</span>;
}

function ProjectCard({ project }) {
  const isForSale = !!project.forSale;
  const useCover = project.logoStyle === 'cover';
  const coverImage = (isForSale || useCover) && project.features && project.features[0]?.screenshot
    ? project.features[0].screenshot
    : null;

  return (
    <Link 
      to={`/realisations/${project.id}`}
      data-testid={`project-card-${project.id}`}
      className="group block"
    >
      <Card className={`h-full relative border shadow-sm hover:border-primary/30 overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:shadow-xl ${
        isForSale 
          ? 'border-amber-400/60 ring-1 ring-amber-300/40 hover:shadow-amber-500/10' 
          : 'border-border hover:shadow-primary/5'
      }`}>
        <CardContent className="p-0">
          {/* Cover area – screenshot for forSale or cover-style, logo for others */}
          {(isForSale || useCover) && coverImage ? (
            <div className="relative h-52 overflow-hidden bg-zinc-900 border-b border-border">
              <img
                src={coverImage}
                alt={`Aperçu ${project.name}`}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/10 pointer-events-none" />

              {/* Category badge */}
              <div className="absolute top-4 left-4 z-20">
                <Badge 
                  variant="outline" 
                  className="text-xs font-semibold border-white/20 bg-black/60 backdrop-blur-sm text-white"
                >
                  {project.type}
                </Badge>
              </div>

              {/* À vendre badge – only for forSale */}
              {isForSale && (
                <div className="absolute top-4 right-4 z-20">
                  <Badge className="bg-amber-500 text-zinc-900 border-amber-400 text-xs font-bold hover:bg-amber-500 shadow-md flex items-center gap-1 px-2.5 py-1">
                    <Tag className="h-3 w-3" />
                    À vendre
                  </Badge>
                </div>
              )}
            </div>
          ) : (
            <div 
              className={`relative h-52 flex items-center justify-center overflow-hidden border-b border-border ${
                project.logoBackground === 'dark' ? 'bg-zinc-900' : 'bg-white'
              }`}
            >
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: `radial-gradient(circle at center, ${project.color}10, transparent 70%)` }}
              />
              {project.logo ? (
                <img
                  src={project.logo}
                  alt={`Logo ${project.name}`}
                  className={`relative z-10 transition-transform duration-500 group-hover:scale-105 ${
                    project.logoStyle === 'app-icon'
                      ? 'h-24 w-24 object-cover rounded-[22%]'
                      : 'max-h-28 max-w-[200px] object-contain'
                  }`}
                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
              ) : null}
              <div className="hidden flex-col items-center gap-2 text-muted-foreground/40">
                <ImageOff className="h-8 w-8" />
              </div>
              
              {/* Category badge */}
              <div className="absolute top-4 left-4 z-20">
                <Badge 
                  variant="outline" 
                  className="text-xs font-semibold border-border/60 bg-background/90 backdrop-blur-sm"
                >
                  {project.type}
                </Badge>
              </div>
              
              {project.featured && (
                <div className="absolute top-4 right-4 z-20">
                  <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs hover:bg-emerald-500/10">
                    Projet phare
                  </Badge>
                </div>
              )}
            </div>
          )}

          {/* Info */}
          <div className="p-6 space-y-3">
            <h3 className="text-xl font-bold tracking-tight group-hover:text-primary transition-colors">
              {project.name}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
              {project.shortDescription}
            </p>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t border-border/50">
              <span className="text-xs text-muted-foreground">{project.year} &middot; {project.duration}</span>
              {isForSale ? (
                <span className="text-xs text-amber-600 font-semibold flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  {project.salePrice || 'À vendre'}
                </span>
              ) : project.category === 'app-mobile' ? (
                <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  iOS & Android
                </span>
              ) : (
                <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Hébergé par Axeris
                </span>
              )}
            </div>
          </div>

          {/* Hover overlay */}
          <div className="absolute inset-0 flex items-end justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
            <div className="w-full p-4 pt-12 bg-gradient-to-t from-background via-background/90 to-transparent">
              <div className="flex items-center justify-center gap-2 text-sm font-semibold text-primary">
                {isForSale ? 'Voir et acquérir' : 'Voir le projet'}
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function RealisationsPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredProjects = useMemo(() => {
    const filtered = projects.filter((p) => {
      if (p.hidden) return false;
      const matchCategory = activeCategory === 'all' || p.category === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || 
        p.name.toLowerCase().includes(q) ||
        p.tagline.toLowerCase().includes(q) ||
        p.technologies.some(t => t.toLowerCase().includes(q)) ||
        p.type.toLowerCase().includes(q);
      return matchCategory && matchSearch;
    });
    // Sites "À vendre" toujours en premier, puis displayOrder
    return filtered.sort((a, b) => {
      const saleSort = (b.forSale ? 1 : 0) - (a.forSale ? 1 : 0);
      if (saleSort !== 0) return saleSort;
      return (a.displayOrder ?? 99) - (b.displayOrder ?? 99);
    });
  }, [activeCategory, searchQuery]);

  return (
    <main data-testid="realisations-page" className="pt-32 pb-0">
      <SEO
        title="Mes réalisations – Projets web sur-mesure"
        description="Découvrez mes projets de création de sites web et d'applications : plateformes e-learning, sites vitrines, SaaS. Chaque projet est conçu sur-mesure à Bourg-en-Bresse, Ain, Lyon et alentours."
        path="/realisations"
      />

      {/* Header */}
      <section className="container-main mb-12">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 animate-fade-up">
          <div className="max-w-3xl">
            <span className="text-sm uppercase tracking-widest font-bold text-primary mb-4 block">
              Portfolio
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none mb-6">
              Mes <span className="text-gradient">réalisations</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Chaque projet est unique. Voici une sélection de sites et applications que j'ai conçus sur-mesure pour mes clients.
            </p>
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="container-main mb-12 animate-fade-up" style={{ animationDelay: '0.1s' }}>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Category filters */}
          <div className="flex flex-wrap items-center gap-2" data-testid="category-filters">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`
                  px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300
                  ${activeCategory === cat.id
                    ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground border border-border/50'
                  }
                `}
              >
                {cat.label}
              </button>
            ))}
            <span className="hidden sm:inline-flex h-5 w-px bg-border/60 mx-1" />
            <span className="hidden sm:inline-flex items-center gap-1.5 text-sm text-muted-foreground font-medium" data-testid="project-counter">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 text-primary text-xs font-bold">
                <AnimatedCounter target={projects.length} duration={1200} />
              </span>
              projets réalisés
            </span>
          </div>

          {/* Search */}
          <div className="relative w-full sm:w-64" data-testid="project-search">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
            />
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="container-main mb-20">
        {filteredProjects.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {filteredProjects.map((project, idx) => (
              <div
                key={project.id}
                className="animate-fade-up"
                style={{ animationDelay: `${0.1 * (idx + 1)}s` }}
              >
                <ProjectCard project={project} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20" data-testid="no-results">
            <p className="text-muted-foreground text-lg mb-2">Aucun projet ne correspond à votre recherche.</p>
            <button
              onClick={() => { setActiveCategory('all'); setSearchQuery(''); }}
              className="text-primary font-semibold hover:underline"
            >
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="bg-muted/30 section-padding">
        <div className="container-main text-center">
          <div className="max-w-2xl mx-auto animate-fade-up">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Votre projet pourrait être le prochain
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-8">
              Que ce soit un site vitrine, une plateforme e-learning ou une application métier, discutons de vos besoins.
            </p>
            <a href="/tarifs#contact">
              <Button className="btn-primary text-lg px-8 py-6" data-testid="realisations-cta">
                Parlons de votre projet
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
